/**
 * Proposal category helpers — single source of truth for the React tree.
 */
import * as Icons from 'lucide-react';
import { publicApi } from './publicApi';

/** Default icon if the spec returns one we don't know about. */
const DEFAULT_ICON = Icons.Briefcase;

/** Returns the lucide-react component for a category's ``icon`` string. */
export const getCategoryIcon = (iconName) => Icons[iconName] || DEFAULT_ICON;

/** Tailwind color tokens for category-card styling. */
export const COLOR_CLASSES = {
  emerald: { bg: 'bg-emerald-50', ring: 'ring-emerald-100', text: 'text-emerald-600' },
  indigo:  { bg: 'bg-indigo-50',  ring: 'ring-indigo-100',  text: 'text-indigo-600'  },
  amber:   { bg: 'bg-amber-50',   ring: 'ring-amber-100',   text: 'text-amber-600'   },
  sky:     { bg: 'bg-sky-50',     ring: 'ring-sky-100',     text: 'text-sky-600'     },
  rose:    { bg: 'bg-rose-50',    ring: 'ring-rose-100',    text: 'text-rose-600'    },
};

export const getCategoryColors = (color) => COLOR_CLASSES[color] || COLOR_CLASSES.emerald;

/** Canonical template gallery routes per proposal category. */
export const PROPOSAL_CATEGORY_ROUTES = {
  business_plan_funding: '/documents/business-proposal/business-plan/templates',
  business_grant_proposal: '/documents/business-proposal/grant-proposal/templates',
  ngo_grant_proposal: '/documents/business-proposal/ngo-grant-proposal/templates',
  service_proposal: '/documents/business-proposal/service-proposal/templates',
};

/** Backend category IDs that are live on the picker (API merge only). */
export const LIVE_PROPOSAL_CATEGORY_IDS = new Set([
  'business_grant_proposal',
  'ngo_grant_proposal',
  'service_proposal',
]);

/** Fixed display order for the Business Proposal Builder category page. */
export const PROPOSAL_CATEGORY_CARD_ORDER = [
  'business_plan_funding',
  'business_grant_proposal',
  'ngo_grant_proposal',
  'service_proposal',
];

const PICKER_CARD_OVERRIDES = {
  business_plan_funding: {
    category_id: 'business_plan_funding',
    display_name: 'Business Plan & Funding',
    description: 'Professional plans for bank loans, investors, and funding.',
    icon: 'Landmark',
    color: 'indigo',
    is_available: false,
    coming_soon_only: true,
  },
  business_grant_proposal: {
    display_name: 'Business Grant Proposal',
    description: 'Create funding proposals for grants, competitions, and business donors.',
  },
  ngo_grant_proposal: {
    display_name: 'NGO Grant Proposal',
    description: 'Nonprofit proposals for foundations, development agencies, and donors.',
  },
  service_proposal: {
    display_name: 'Service Proposal',
    description: 'Generate corporate proposals for contracts and service bids.',
  },
};

/**
 * Build the 3 category cards for ProposalCategoryPage (display + API merge).
 */
export const buildProposalCategoryCards = (apiCategories = []) => {
  const byId = Object.fromEntries(
    (apiCategories || []).map((c) => [c.category_id, c]),
  );

  return PROPOSAL_CATEGORY_CARD_ORDER.map((id) => {
    const override = PICKER_CARD_OVERRIDES[id] || {};
    if (override.coming_soon_only) {
      return {
        ...override,
        credit_cost: null,
      };
    }

    const api = byId[id] || {};
    return {
      category_id: id,
      display_name: override.display_name || api.display_name || id,
      description: override.description || api.description || '',
      icon: api.icon || 'Briefcase',
      color: api.color || (
        id === 'service_proposal' ? 'sky'
          : id === 'ngo_grant_proposal' ? 'amber'
            : 'emerald'
      ),
      is_available: Boolean(api.is_available),
      credit_cost: api.credit_cost ?? 20,
      coming_soon_only: false,
    };
  });
};

/**
 * Routes a category selection to the correct template gallery.
 */
export const routeForCategory = (cat) => {
  if (!cat) return '/services/business-proposal-builder';
  if (PROPOSAL_CATEGORY_ROUTES[cat.category_id]) {
    return PROPOSAL_CATEGORY_ROUTES[cat.category_id];
  }
  if (!cat.is_available) {
    return `/documents/business-proposal/coming-soon?category=${cat.category_id}`;
  }
  return '/services/business-proposal-builder';
};

/** Fetch all categories from the backend (public listing). */
export const fetchProposalCategories = async () => {
  const res = await publicApi.get('/proposals/categories');
  return res.data?.categories || [];
};

/** Fetch a single category's full spec (including wizard schema). */
export const fetchProposalCategory = async (api, categoryId) => {
  const res = await api.get(`/proposals/categories/${categoryId}`);
  return res.data;
};
